import type { BaseReactPlayerProps } from 'react-player/base';
import styled, { css } from 'styled-components';
import type ReactPlayerType from 'react-player/lazy';
import { forwardRef, useCallback, useContext, useState } from 'react';
import dynamic from 'helpers/dynamic';
import useIsMobile from 'hooks/responsive/useIsMobile';
import toast from 'components/Toast';
import { GeneralVideoExceptionError } from 'autogen/translation-keys/trans-website-shared';
import { useTranslation } from 'react-i18next';
import type { IVideo } from './shared/types';
import { getVideoConfig, getStartTimeFromVideoUrl } from './shared/helpers';
import useUnavailableVideos from './hooks/useUnavailableVideos';
import VideoCookieOverlay from 'components/VideoCookieOverlay/VideoCookieOverlay';
import useHasInformationCookieBeenAccepted from 'hooks/user/useHasInformationCookieBeenAccepted';
import { CookieCategory } from 'modules/helpers/cookieInformationHelper';
import { ColorNeutralBlack } from 'autogen/design-tokens/tokens';
import useRequestContext from 'hooks/user/useRequestContext';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';
import type { ResponsiveImageModel } from 'autogen/swagger/all';
import useGA4Tracking from 'modules/tracking/GA4';
import type { VideoControlTypes } from 'modules/tracking/GA4/types/VideoTypes';
import { Context as TrackingContext } from 'modules/tracking/GA4/helpers/TrackingContext';

export interface ICustomPlayer extends BaseReactPlayerProps {
    playing?: boolean;
    video: IVideo;
    videoContainerClassName: string;
    onError?: () => void;
    image?: ResponsiveImageModel;
}

const VideoContainer = styled.div<{ $cookiesAccepted?: boolean }>`
    display: block;
    position: relative;
    width: 100%;
    height: 100%;

    & iframe {
        width: 100%;
        height: 100%;
    }

    ${({ $cookiesAccepted }) =>
        !$cookiesAccepted &&
        css`
                background: ${ColorNeutralBlack};
            `}
`;

const ReactPlayer = styled(dynamic(() => import('react-player/lazy'), { ssr: false }))`
    display: flex;
    width: 100% !important;
    height: 100% !important;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    border: 0;

    & > div {
        display: flex;
    }
`;

const ImageContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;

    & > picture > img {
        width: 100%;
        height: 100%;
    }
`;

const VideoPlayer = forwardRef<ReactPlayerType, ICustomPlayer>(
    ({ playing = true, video, onError, videoContainerClassName, children, image, ...restProps }, ref) => {
        const { t } = useTranslation();
        const { url, platform } = video;
        const videoConfig = getVideoConfig(video);
        const { setUnavailableVideo } = useUnavailableVideos();
        const isMobile = useIsMobile();
        const { isClubMatasApp } = useRequestContext();
        const isFunctionalCookieAccepted = useHasInformationCookieBeenAccepted(CookieCategory.functional);
        const GA4Tracking = useGA4Tracking();
        const [playedSeconds, setPlayedSeconds] = useState(0);
        const { context } = useContext(TrackingContext) || {};

        const trackControl = useCallback(
            (controlInput: VideoControlTypes) => {
                GA4Tracking({
                    eventName: 'click',
                    category: 'advisory',
                    context: context || 'none', // 'none' in case of missing contexts from TrackingContext.
                    eventParams: [
                        { param_name: 'video_controls', param_value: controlInput },
                        { param_name: 'video_progress', param_value: playedSeconds }
                    ]
                });
            },
            [GA4Tracking, playedSeconds, context]
        );

        const handleError = (error) =>
            new Promise<void>((resolve) => {
                console.error(new Error('Unable to play or load the video'), error);
                setUnavailableVideo(video.id);

                if (playing) {
                    toast.error<string>(t(GeneralVideoExceptionError), {
                        position: 'top-right'
                    });
                }
                resolve();
            }).then(() => typeof onError === 'function' && onError());

        if (!isFunctionalCookieAccepted && !isClubMatasApp) {
            return (
                <VideoContainer className={videoContainerClassName} $cookiesAccepted={false}>
                    <ImageContainer>
                        <PictureDeprecated {...image} />
                    </ImageContainer>
                    <VideoCookieOverlay />
                </VideoContainer>
            );
        }

        return (
            <VideoContainer className={videoContainerClassName}>
                <ReactPlayer
                    ref={ref as any}
                    url={url}
                    playing={playing}
                    config={videoConfig || undefined}
                    muted={isMobile} // Videos won't play autoplay on IOS if they are not muted
                    playsinline
                    controls
                    onError={handleError}
                    onReady={({ seekTo }) => {
                        const explicitStartTime = getStartTimeFromVideoUrl(url);
                        if (explicitStartTime) seekTo(explicitStartTime, 'seconds');
                    }}
                    {...restProps}
                    onPlay={() => trackControl('play')}
                    onStart={() => trackControl('start')}
                    onPause={() => trackControl('pause')}
                    onProgress={(progress) => setPlayedSeconds(progress.playedSeconds)}
                />
                {children}
            </VideoContainer>
        );
    }
);

export default VideoPlayer;
